/**
 * Normaliza texto para los rieles que no toleran nada fuera del ASCII básico.
 *
 * El caso concreto: el nombre del beneficiario se guarda tal cual se tipea y
 * recién viaja al proveedor en el PAYOUT (el envío solo manda el
 * `beneficiarioId`; el backend levanta el nombre de la base). Si quedó con
 * tilde, el alta pasa sin una queja y el error explota días después, al
 * enviar, lejos del formulario que lo causó.
 *
 * El acento no se rechaza: se limpia. Bloquear obliga al cliente a inventar
 * una grafía ("PERES" en vez de "PEREZ") y un nombre mal escrito es peor que
 * uno sin tilde.
 */

/**
 * Las que NO se descomponen con `NFD` — no son letra + diacrítico, son
 * caracteres propios. Se transliteran a mano o sobrevivirían al filtro.
 */
const SIN_DESCOMPOSICION: Record<string, string> = {
  Æ: 'AE',
  æ: 'ae',
  Ø: 'O',
  ø: 'o',
  Œ: 'OE',
  œ: 'oe',
  ß: 'ss',
  Đ: 'D',
  đ: 'd',
  Ł: 'L',
  ł: 'l',
};

/** Marcas diacríticas que `normalize('NFD')` deja sueltas detrás de la letra. */
const DIACRITICOS = /[̀-ͯ]/g;

/**
 * `JOSÉ PÉREZ` → `JOSE PEREZ`. Cubre tildes, diéresis y cedilla.
 *
 * La **Ñ cae acá también** (`NIÑA` → `NINA`) y es a propósito: `NFD` la parte
 * en `N` + tilde combinante. No es un descuido — ningún riel que rechace
 * acentos acepta la eñe, y mandar `NIÑA` es garantía de rebote en el envío.
 */
export function sinTildes(valor: string): string {
  return valor
    .replace(/[ÆæØøŒœßĐđŁł]/g, (c) => SIN_DESCOMPOSICION[c] ?? c)
    .normalize('NFD')
    .replace(DIACRITICOS, '');
}
